import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import {
    ProductAssistantWidget,
    type WidgetContainerProps,
    type WidgetContainerRuntime
} from '../components/WidgetContainer';
import { PortalContainerContext, type PortalContainer } from '../components/PortalContainerContext';
import { i18nInstance } from '../localization/i18n';

export type ProductAssistantWidgetHandle = {
    unmount: () => void;
    update: (nextProps?: Partial<WidgetContainerProps>) => void;
    send: (message: string) => boolean;
};

export function mountProductAssistantWidget(target: HTMLElement, props?: WidgetContainerProps): ProductAssistantWidgetHandle {
    if (!(target instanceof HTMLElement)) {
        throw new Error('A valid HTMLElement is required to mount the Product Assistant widget.');
    }

    let currentProps: WidgetContainerProps = {
        ...props
    };
    let runtime: WidgetContainerRuntime | null = null;

    const mountTarget = resolveMountTarget(target);
    const root = ReactDOM.createRoot(mountTarget.reactContainer);

    const render = () => {
        root.render(
            <PortalContainerContext.Provider value={mountTarget.portalContainer}>
                <I18nextProvider i18n={i18nInstance}>
                    <ProductAssistantWidget
                        {...currentProps}
                        onRuntimeReady={(nextRuntime) => {
                            runtime = nextRuntime;
                        }}
                    />
                </I18nextProvider>
            </PortalContainerContext.Provider>
        );
    };

    render();

    return {
        unmount() {
            runtime = null;
            root.unmount();
        },
        update(nextProps) {
            if (!nextProps) {
                return;
            }

            currentProps = {
                ...currentProps,
                ...nextProps
            };

            render();
        },
        send(message: string): boolean {
            if (!runtime) {
                throw new Error('Widget runtime is not ready. Wait for the widget to mount before calling send().');
            }

            return runtime.send(message);
        }
    };
}

type MountTarget = {
    reactContainer: HTMLElement;
    portalContainer: PortalContainer;
};

const ShadowAppContainerAttribute = 'data-skf-widget-app-container';
const ShadowPortalContainerAttribute = 'data-skf-widget-portal-container';
const ShadowStylesheetAttribute = 'data-skf-widget-shadow-stylesheet';
const ShadowInlineStyleAttribute = 'data-skf-widget-shadow-inline-style';
const WidgetStylesheetFileName = 'product-assistant-widget.css';

function resolveMountTarget(target: HTMLElement): MountTarget {
    const lightDomTarget = createLightDomMountTarget(target);

    if (typeof target.attachShadow !== 'function') {
        return lightDomTarget;
    }

    try {
        const shadowRoot = target.shadowRoot ?? target.attachShadow({ mode: 'open' });
        ensureShadowRootStyles(shadowRoot);

        const reactContainer = getOrCreateShadowElement(shadowRoot, ShadowAppContainerAttribute);
        const portalContainer = getOrCreateShadowElement(shadowRoot, ShadowPortalContainerAttribute);

        return {
            reactContainer,
            portalContainer
        };
    } catch {
        return lightDomTarget;
    }
}

function createLightDomMountTarget(target: HTMLElement): MountTarget {
    return {
        reactContainer: target,
        portalContainer: target.ownerDocument.body ?? target
    };
}

function getOrCreateShadowElement(shadowRoot: ShadowRoot, attribute: string): HTMLDivElement {
    const existing = shadowRoot.querySelector(`[${attribute}]`);
    if (existing instanceof HTMLDivElement) {
        applyShadowElementLayout(existing, attribute);
        return existing;
    }

    const element = document.createElement('div');
    element.setAttribute(attribute, 'true');
    applyShadowElementLayout(element, attribute);
    shadowRoot.appendChild(element);
    return element;
}

function applyShadowElementLayout(element: HTMLDivElement, attribute: string): void {
    if (attribute === ShadowAppContainerAttribute) {
        // Host containers may be flex layouts (for dock regions). Ensure the
        // widget mount container stretches instead of shrink-to-fit.
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.minWidth = '0';
        element.style.minHeight = '0';
        element.style.flex = '1 1 auto';
        element.style.alignSelf = 'stretch';
        return;
    }

    if (attribute === ShadowPortalContainerAttribute) {
        element.style.width = '0';
        element.style.height = '0';
        element.style.flex = '0 0 auto';
        element.style.alignSelf = 'stretch';
    }
}

function ensureShadowRootStyles(shadowRoot: ShadowRoot): void {
    const stylesheetHrefs = resolveWidgetStylesheetHrefs();
    const managedStylesheetLinks = Array.from(
        shadowRoot.querySelectorAll(`link[rel~="stylesheet"][href][${ShadowStylesheetAttribute}]`)
    ) as HTMLLinkElement[];

    for (const managedStylesheetLink of managedStylesheetLinks) {
        const href = normalizeUrl(managedStylesheetLink.getAttribute('href'));
        if (!href || !stylesheetHrefs.includes(href)) {
            managedStylesheetLink.remove();
        }
    }

    for (const stylesheetHref of stylesheetHrefs) {
        const existingLink = Array.from(
            shadowRoot.querySelectorAll('link[rel~="stylesheet"][href]')
        ).find((link) => normalizeUrl(link.getAttribute('href')) === stylesheetHref);

        if (existingLink) {
            continue;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = stylesheetHref;
        link.setAttribute(ShadowStylesheetAttribute, 'true');
        shadowRoot.appendChild(link);
    }

    if (stylesheetHrefs.length > 0) {
        return;
    }

    if (shadowRoot.querySelector(`style[${ShadowInlineStyleAttribute}]`)) {
        return;
    }

    const inlineStyles = collectWidgetInlineStyles();
    if (!inlineStyles.length) {
        return;
    }

    const style = document.createElement('style');
    style.setAttribute(ShadowInlineStyleAttribute, 'true');
    style.textContent = inlineStyles.join('\n');
    shadowRoot.appendChild(style);
}

function resolveWidgetStylesheetHrefs(): string[] {
    const stylesheetLinks = Array.from(
        document.querySelectorAll('link[rel~="stylesheet"][href]')
    ) as HTMLLinkElement[];
    const fileNameMatches: string[] = [];
    const scopedSelectorMatches: string[] = [];

    for (const stylesheetLink of stylesheetLinks) {
        const href = stylesheetLink.getAttribute('href');
        const normalizedHref = normalizeUrl(href);
        if (!normalizedHref) {
            continue;
        }

        if (normalizedHref.toLowerCase().includes(WidgetStylesheetFileName)) {
            pushUnique(fileNameMatches, normalizedHref);
            continue;
        }

        if (hasWidgetScopedSelectors(stylesheetLink)) {
            pushUnique(scopedSelectorMatches, normalizedHref);
        }
    }

    if (fileNameMatches.length > 0) {
        return [fileNameMatches[fileNameMatches.length - 1]];
    }

    if (scopedSelectorMatches.length > 0) {
        return [scopedSelectorMatches[scopedSelectorMatches.length - 1]];
    }

    const scriptTags = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];

    for (let index = scriptTags.length - 1; index >= 0; index -= 1) {
        const source = scriptTags[index].src;
        const inferredHrefs = deriveStylesheetHrefsFromWidgetSource(source);
        if (inferredHrefs.length > 0) {
            return inferredHrefs;
        }
    }

    return [];
}

function hasWidgetScopedSelectors(link: HTMLLinkElement): boolean {
    const styleSheet = link.sheet;
    if (!styleSheet || !('cssRules' in styleSheet)) {
        return false;
    }

    try {
        const cssRules = Array.from(styleSheet.cssRules);
        const maxRulesToScan = 200;
        const ruleCount = Math.min(cssRules.length, maxRulesToScan);

        for (let index = 0; index < ruleCount; index += 1) {
            const cssText = cssRules[index].cssText;
            if (isWidgetStyleText(cssText)) {
                return true;
            }
        }
    } catch {
        return false;
    }

    return false;
}

function deriveStylesheetHrefsFromWidgetSource(source: string): string[] {
    const url = normalizeUrl(source);
    if (!url) {
        return [];
    }

    try {
        const parsedUrl = new URL(url);
        const normalizedPath = parsedUrl.pathname.toLowerCase();
        const candidates: string[] = [];

        if (normalizedPath.endsWith('/browser/widget.js')) {
            parsedUrl.pathname = parsedUrl.pathname.slice(0, -'/browser/widget.js'.length) + `/${WidgetStylesheetFileName}`;
            parsedUrl.search = '';
            parsedUrl.hash = '';
            pushUnique(candidates, parsedUrl.toString());
            return candidates;
        }

        if (normalizedPath.endsWith('/widget.js')) {
            parsedUrl.pathname = parsedUrl.pathname.slice(0, -'/widget.js'.length) + `/${WidgetStylesheetFileName}`;
            parsedUrl.search = '';
            parsedUrl.hash = '';
            pushUnique(candidates, parsedUrl.toString());
            return candidates;
        }
    } catch {
        return [];
    }

    return [];
}

function normalizeUrl(value: string | null): string | null {
    if (!value) {
        return null;
    }

    try {
        return new URL(value, window.location.href).toString();
    } catch {
        return null;
    }
}

function collectWidgetInlineStyles(): string[] {
    const styles = Array.from(document.querySelectorAll('style'));
    const inlineStyles: string[] = [];

    for (const style of styles) {
        const cssText = style.textContent;
        if (!cssText || !isWidgetStyleText(cssText)) {
            continue;
        }

        inlineStyles.push(cssText);
    }

    return inlineStyles;
}

function isWidgetStyleText(cssText: string): boolean {
    return cssText.includes('skf-pa-') || cssText.includes('.katex') || cssText.includes('SKF Office Sans');
}

function pushUnique(target: string[], value: string): void {
    if (!target.includes(value)) {
        target.push(value);
    }
}
